import React, { useContext } from 'react';

import cx from 'classnames';
import { isEmpty, isNil } from 'ramda';
import { useHistory, useParams } from 'react-router-dom';

import { IRoom } from 'components/shared/Assets/shared/Form/Document/types';
import { PAGE_ROOMS_PATH } from 'constants/routes';
import { useAccountPath } from 'hooks/useAccountPath';
import { confirmAlert } from 'utils/alert';
import { isValidVideoUrl } from 'utils/parseVideoUrl';

import LandingPageContext from '../../contexts/LandingPageContext';
import { IUploadData } from '../AddNewContent/Upload/types';
import Image from '../Image';
import Text from '../Text';
import Video from '../Video';
import s from './index.module.css';
import { IRoomProps } from './types';

function Room({ id, data = [], handleClick }: IRoomProps) {
  return (
    <div className={cx(s.room, 'custom-room')} onClick={() => handleClick(id)}>
      {data.map((roomData, i) => {
        // @ts-ignore
        if (roomData.type === 'boldText') return <Text key={i} data={roomData} />;
        if (roomData.type === 'text')
          return (
            <div key={i} className={cx(s.wrapper, 'custom-room-wrapper')} data-block>
              {/*@ts-ignore*/}
              <Text data={roomData} />
            </div>
          );
        if (roomData.type === 'image')
          return (
            <div key={i} className={cx(s.wrapper, s.images, 'custom-room-images')}>
              <Image data={roomData} />
            </div>
          );
        if (roomData.type === 'video') {
          const videos = roomData.data?.files?.filter((videoData) => isValidVideoUrl(videoData.link as string));
          if (!videos || isEmpty(videos)) {
            return null;
          }

          return (
            <div key={i} className={cx(s.videos, 'custom-room-videos')}>
              {videos.map((videoData) => (
                <div key={videoData.id} className={cx(s.wrapper, 'custom-room-wrapper')}>
                  <Video data={{ data: { source: videoData.link } }} />
                </div>
              ))}
            </div>
          );
        }

        return null;
      })}
    </div>
  );
}

function Accommodations() {
  const { readOnly, page } = useContext(LandingPageContext);
  const rooms = 'rooms' in page ? getFormattedRooms(page?.rooms) : [];
  const getPath = useAccountPath();
  const history = useHistory();
  const { pageId } = useParams<{ pageId: string }>();

  const handleClick = (roomId: number) => {
    if (readOnly) return;
    const roomsPath = `${getPath(PAGE_ROOMS_PATH, { pageId })}?id=${roomId}`;

    confirmAlert({
      title: (
        <>
          Edits to this page can only be done in the Rooms Tab. <br /> Do you want to go there now?
        </>
      ),
      confirmButtonText: 'Go to Rooms Tab',
      onConfirm: () => {
        history.push(roomsPath);
      },
    });
  };

  return (
    // @ts-ignore
    <LandingPageContext.Provider value={{ readOnly: true }}>
      <div className={cx(s.collection, 'custom-room-collection')}>
        {rooms.map((room) => {
          return <Room key={room.id} id={room.id!} data={room.data as IUploadData[]} handleClick={handleClick} />;
        })}
      </div>
    </LandingPageContext.Provider>
  );
}

export default Accommodations;

function getFormattedRooms(rooms: IRoom[] = []) {
  return rooms.map((room: IRoom) => {
    const data = [];

    if (!isNil(room?.name)) {
      const name = room?.name || '';

      data.push({
        type: 'boldText',
        data: {
          raw: {
            blocks: [
              {
                key: 'frbl6',
                text: name,
                type: 'unstyled',
                depth: 0,
                inlineStyleRanges: [{ offset: 0, style: 'BOLD', length: name.length }],
                entityRanges: [],
                data: {},
              },
            ],
            entityMap: {},
          },
        },
      });
    }
    if (!isNil(room?.description)) {
      data.push({
        type: 'text',
        data: {
          text: room?.description || '',
        },
      });
    }
    if (room?.media) {
      data.push({
        type: 'image',
        data: {
          files: room?.media || [],
        },
      });
    }
    if (room?.videos) {
      data.push({
        type: 'video',
        data: {
          files: room?.videos || [],
        },
      });
    }

    return { id: room.id, data };
  });
}
